import * as AdaptiveCards from "adaptivecards";
import markdownit from "markdown-it";
import {ITranslationMessage} from "../../Interfaces/TranslationMessage";
import {IDraftMessage} from "../../Interfaces/Message";
import {ITopic} from "../../Interfaces/Topic";
import {ILanguage} from "../../Interfaces/Language";
import languageObject from "../../translations";

interface IFeedbackButtonData {
    title: string,
    placeholder: string,
    sendBtn: string
}

enum AdaptiveCardPart {
    Title = 0,
    Banner = 1,
    Summary = 2,
    Image = 3,
    SubMessage = 4,
    Author = 5,
    Topics = 6,
    Buttons = 7,
    Feedback = 8
}

export const getInitAdaptiveCard = () => {
    const titleTextAsString = languageObject.translate("TitleText");
    return (
        {
            "type": "AdaptiveCard",
            "body": [
                {
                    "id": "title",
                    "type": "TextBlock",
                    "weight": "Bolder",
                    "text": titleTextAsString,
                    "size": "ExtraLarge",
                    "wrap": true
                },
                {
                    "id": "banner",
                    "type": "Image",
                    "url": "",
                    "size": "Stretch",
                    "altText": ""
                },
                {
                    "id": "summary",
                    "type": "TextBlock",
                    "text": "",
                    "wrap": true
                },
                {
                    "id": "image",
                    "type": "Image",
                    "url": "",
                    "size": "Stretch",
                    "altText": ""
                },
                {
                    "id": "subMessage",
                    "type": "TextBlock",
                    "text": "",
                    "wrap": true
                },
                {
                    "id": "author",
                    "type": "TextBlock",
                    "wrap": true,
                    "size": "Small",
                    "weight": "Lighter",
                    "text": ""
                },
                {
                    "id": "topics",
                    "type": "TextBlock",
                    "text": "",
                    "wrap": true,
                    "separator": true,
                    "horizontalAlignment": "Right",
                    "size": "Small"
                },
                {
                    "id": "buttons",
                    "type": "ActionSet",
                    "actions": []
                },
            ],
            "$schema": "https://adaptivecards.io/schemas/adaptive-card.json",
            "version": "1.3"
        }
    );
}

export const setCardTitle = (card: any, title: string) => {
    card.body[AdaptiveCardPart.Title].text = title;
}

export const setCardImageLink = (card: any, imageLink?: string) => {
    card.body[AdaptiveCardPart.Image].url = imageLink;
}

export const setCardBannerLink = (card: any, bannerLink?: string) => {
    card.body[AdaptiveCardPart.Banner].url = bannerLink;
}

export const setCardSummary = (card: any, summary?: string) => {
    card.body[AdaptiveCardPart.Summary].text = summary;
}

export const setCardSubMessage = (card: any, subMessage?: string) => {
    card.body[AdaptiveCardPart.SubMessage].text = subMessage;
}

export const setCardAuthor = (card: any, author?: string) => {
    card.body[AdaptiveCardPart.Author].text = author;
}

export const setCardFeedback = (card: any, feedbackData?: IFeedbackButtonData) => {
    if (feedbackData) {
        if (card.body[card.body.length - 1].id === "feedbackContainer") {
            card.body.splice(card.body.length - 1, 1);
            card.body[card.body.length - 1].actions.shift();
        }
        card.body.push({
            "type": "Container",
            "id": "feedbackContainer",
            "items": [
                {
                    "type": "Input.Text",
                    "placeholder": feedbackData.placeholder,
                    "id": "feedbackInput",
                    "isMultiline": true
                },
                {
                    "type": "ActionSet",
                    "actions": [
                        {
                            "type": "Action.Submit",
                            "title": feedbackData.sendBtn,
                            "id": "submitFeedback",
                        }
                    ]
                }
            ],
            "isVisible": false,
            "style": "emphasis",
            "bleed": false
        });
        card.body[card.body.length - 2].actions.push({
            "type": "Action.ToggleVisibility",
            "title": feedbackData.title,
            "targetElements": [
                "feedbackContainer"
            ]
        })
    } else {
        if (card.body[card.body.length - 1].id === "feedbackContainer") {
            card.body.splice(card.body.length - 1, 1);
            card.body[card.body.length - 1].actions.shift();
        }
    }
}

export const executeAction = (action: any) => {
    const actionId = action.id;
    if (!actionId) return;
    if (actionId === "submitFeedback") alert("Feedback sent");
    else if (actionId.startsWith("customButton")) window.open(action.url, '_blank');
}

export const clearCardButtons = (card: any) => {
    if (card.body[card.body.length - 1].id === "feedbackContainer")
        card.body[card.body.length - 2].actions.length = 1;
    else card.body[card.body.length - 1].actions = [];
}

export const setCardBtn = (card: any, index: number, buttonTitle: string, buttonLink: string) => {
    const buttonData = {
        "type": "Action.OpenUrl",
        "title": buttonTitle,
        "url": buttonLink,
        "id": "customButton" + index,
    };
    if (card.body[card.body.length - 1].id === "feedbackContainer") {
        card.body[card.body.length - 2].actions.push(buttonData);
    } else {
        card.body[card.body.length - 1].actions.push(buttonData);
    }
}

export const setCardTopicsText = (card: any, text: string) => {
    for (let i = 0; i < card.body.length; i++) {
        if (card.body[i] && card.body[i].id && card.body[i].id === "topics") {
            card.body[i].text = text;
        }
    }
}

export const needToShowDefaultCard = (data: ITranslationMessage) => {
    return !data.title && !data.imageLink && !data.message && !data.author && data.buttons.length === 0 &&
        !data.askFeedback;
}

const getTopicsTitles = (draftMessage: IDraftMessage, topics: Array<ITopic>, currentData: number) => {
    const data = currentData === -1 ? draftMessage.defaultLanguage : draftMessage.translations[currentData];
    let topicsTitles;
    if (currentData === -1) {
        topicsTitles = draftMessage.topicsId.map(tid => topics.find(t => t.id === tid)?.title).join(", ")
    } else {
        const currentLanguageId = data.id;
        const titles = new Array<string>();
        draftMessage.topicsId.forEach(tid => {
            const topic = topics.find(t => t.id === tid);
            if (topic) {
                const translation = topic.translations && topic.translations.filter(t => t.languageId === currentLanguageId)[0];
                titles.push(translation ? translation.title : topic.title);
            }
        });
        topicsTitles = titles.join(", ");
    }
    return topicsTitles;
}

export const setDefaultWelcomeCard = (card: any) => {
    setCardTitle(card, languageObject.translate("TitleText"));
    setCardSummary(card, languageObject.translate("Summary"));
}

export const setDefaultCard = (
    card: any, draftMessage: IDraftMessage, topics: Array<ITopic>,
    languages: Array<ILanguage>, currentData: number
) => {
    const titleAsString = languageObject.translate("TitleText");
    const summaryAsString = languageObject.translate("Summary");
    const authorAsString = languageObject.translate("Author");
    setCardTitle(card, titleAsString);
    setCardSummary(card, summaryAsString);
    setCardAuthor(card, authorAsString);
    setCardTopicsText(card, getTopicsTitles(draftMessage, topics, currentData));
    clearCardButtons(card);
    setCardFeedback(card);
    setCardImageLink(card);
    setCardBannerLink(card);
    setCardSubMessage(card);
}

export const updateWelcomeCard = (card: any, title: string, image: string, message: string) => {
    if (!title && !image && !message) {
        setDefaultWelcomeCard(card);
    } else {
        setCardTitle(card, title);
        setCardSummary(card, message);
        setCardImageLink(card, image);
    }
    const adaptiveCard = new AdaptiveCards.AdaptiveCard();
    AdaptiveCards.AdaptiveCard.onProcessMarkdown = function (text, result) {
        result.outputHtml = markdownit().render(text);
        result.didProcess = true;
    };
    adaptiveCard.parse(card);
    const renderedCard = adaptiveCard.render();
    const container = document.querySelector('.welcomeAdaptiveCardContainer');
    if (!!container && renderedCard) {
        if (!!container?.firstChild) container.firstChild.replaceWith(renderedCard);
        else container?.appendChild(renderedCard);
    }
}

export const updateCard = (card: any, draftMessage: IDraftMessage, topics: Array<ITopic>, languages: Array<ILanguage>,
                           currentData: number = -1) => {
    const data = currentData === -1 ? draftMessage.defaultLanguage : draftMessage.translations[currentData];
    if (needToShowDefaultCard(data))
        setDefaultCard(card, draftMessage, topics, languages, currentData);
    else {
        setCardTitle(card, data.title);
        setCardSummary(card, data.message);
        setCardSubMessage(card, data.subMessage);
        setCardAuthor(card, data.author);
        setCardImageLink(card, data.image ? data.image.content : data.imageLink);
        setCardBannerLink(card, data.banner ? data.banner.content : data.bannerLink);
        setCardTopicsText(card, getTopicsTitles(draftMessage, topics, currentData));
        clearCardButtons(card);
        data.buttons.forEach((button, index) => {
            if (button.title && button.link && button.link.startsWith("https://"))
                setCardBtn(card, index, button.title, button.link);
        });
        if (data.askFeedback)
            setCardFeedback(card, getFeedbackData(languages.find(l => l.id === data.id)));
        else setCardFeedback(card);
    }
    const adaptiveCard = new AdaptiveCards.AdaptiveCard();
    AdaptiveCards.AdaptiveCard.onProcessMarkdown = function (text, result) {
        result.outputHtml = markdownit().render(text);
        result.didProcess = true;
    };
    adaptiveCard.parse(card);
    const renderedCard = adaptiveCard.render();
    const container = document.querySelector('.adaptiveCardContainer');
    if (!!container && renderedCard) {
        if (!!container?.firstChild) container.firstChild.replaceWith(renderedCard);
        else container?.appendChild(renderedCard);
    }
    adaptiveCard.onExecuteAction = function (action: any) {
        executeAction(action);
    }
}

const getFeedbackData = (language?: ILanguage): IFeedbackButtonData => {
    switch (language?.code) {
        case "FR":
            return {
                title: "📣 Faites-nous un retour",
                placeholder: "Dites-nous à quoi vous pensez",
                sendBtn: "Envoyer",
            }
        case "ES":
            return {
                title: "📣 Denos su opinión",
                placeholder: "Dinos qué piensas",
                sendBtn: "Envíe sus comentarios",
            }
        case "IT":
            return {
                title: "📣 Dacci un feedback",
                placeholder: "Diteci cosa ne pensate",
                sendBtn: "Invia feedback",
            }
        case "DE":
            return {
                title: "📣 Gib uns ein feedback",
                placeholder: "Sagen Sie uns was Sie denken",
                sendBtn: "📣 Gib uns ein feedback",
            }
        case "PT":
            return {
                title: "📣 Dê-nos um feedback ",
                placeholder: "Nos diga o que você acha",
                sendBtn: "Feedback abschicken",
            }
        default:
            return {
                title: "📣 Give some feedback",
                placeholder: "Tell us what you think",
                sendBtn: "Send feedback",
            }
    }
}